import { OrderDetailsSummary } from './OrderDetailsSummary';
import { CreditFieldsAndDeomgraphicsSummary } from './CreditFieldsAndDemographicsSummary';
import { InputFileSummary } from './InputFileSummary';
import { OutputLocationsSummary } from './OutputLocationsSummary';
import { ExperianIcon } from '@/app/features/products/summary/ExperianIcon';
import { modalUtils } from '@/app/core/utils';
import {
  getAttributes,
  getBundles,
  getFullAttributes,
  getScores,
  getTrendviewFields,
  getAddonProducts,
} from '@/app/features/products/common';
import { ProgramSummaryFooter } from '@/app/features/products/summary/ProgramSummaryFooter';
import {
  getFullAttributesWithDetailsAndRowColor,
  getOutputLayoutFieldsWithDetailsAndRowColor,
  getScoreDistributionFields,
} from '@/app/features/products/common/services/program-service';
import { OutputFileLayoutSummary } from '@/app/features/products/summary/OutputFileLayoutSummary';
import { InquiryLoggingSummary } from '@/app/features/products/summary/InquiryLoggingSummary';
import { ScoreDistributionReport } from '@/app/features/products/summary/ScoreDistributionReport';
import {
  FieldDelimiter,
  HasRowColor,
  OutputLayoutFieldWithDetails,
  ProgramDataType,
  SSARProgram,
  Job,
  AttributeLookup,
  BundleLookup,
  FullAttributeLookup,
  ScoreLookup,
  TrendviewLookup,
  AddonProductLookup,
} from '@ssar-app/domain';
import { useEffect, useState, Fragment } from 'react';
import { Modal, Spin } from '@ssar-app/uikit';
import { Flex, Text } from '@chakra-ui/react';

export interface ProgramSummaryModalProps {
  model: ProgramDataType;
  job?: Job;
  version: string;
  onCancel?: () => void;
  showAccept?: boolean;
  onAccept?: () => void;
  onOk?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDownload?: (arg0: any) => void;
  closeModal: () => void;
}

interface SummaryDataContainer {
  scoreLookups: ScoreLookup[];
  bundleLookups: BundleLookup[];
  trendviewFieldLookups: TrendviewLookup[];
  addonProductLookups: AddonProductLookup[];
  attributeLookups: AttributeLookup[];
  fullAttributeLookups: FullAttributeLookup[];
  outputLayoutFieldsWithDetailsAndRowColor: Record<
    string,
    HasRowColor<OutputLayoutFieldWithDetails>[]
  >;
  fullAttributesWithDetailsAndRowColor: Record<
    string,
    HasRowColor<OutputLayoutFieldWithDetails>[]
  >;
  scoreDistributionFields: HasRowColor<OutputLayoutFieldWithDetails>[];
}
export const ProgramSummaryModal = (props: ProgramSummaryModalProps) => {
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [summaryDataContainer, setSummaryDataContainer] =
    useState<SummaryDataContainer>();
  const summaryModel = props.model as SSARProgram;

  useEffect(() => {
    const { fields: outputLayoutFields, delimiter: outputDelimiter } =
      summaryModel.outputLayout || {};
    const subCode = summaryModel.subCode;
    const version = props.version;
    setIsLoadingData(true);
    Promise.all([
      getBundles(version, subCode),
      getScores(version, subCode),
      getTrendviewFields(version, subCode),
      getAddonProducts(version, subCode),
      getAttributes(version, subCode),
      getFullAttributes(version, subCode),
      getOutputLayoutFieldsWithDetailsAndRowColor(
        summaryModel.subCode || '',
        version,
        {
          outputFields: outputLayoutFields || [],
          inputFields: summaryModel?.inputFile?.fieldsInfo?.fields || [],
        },
        outputDelimiter || FieldDelimiter.COMMA,
      ),
      getFullAttributesWithDetailsAndRowColor(
        props.model?.subCode || '',
        version,
        summaryModel?.attributesAndBundles?.fullAttributes || [],
      ),
      getScoreDistributionFields(
        summaryModel.subCode || '',
        version,
        summaryModel.reports?.distribution?.attributes || [],
      ),
    ])
      .then((responses) => {
        const failedResponses = responses.filter((a) => a.resultCode !== 0);
        if (failedResponses.length > 0) {
          let errors: string[] = [];
          failedResponses.forEach((a) => {
            errors = errors.concat(a.messages);
          });
          throw new Error(`Error while loading lookups:${errors.join('\\n')}`);
        } else {
          const [
            bundlesResponse,
            scoresResponse,
            trendviewFieldsResponse,
            addonProductsResponse,
            attributesResponse,
            fullAttributesResponse,
            outputLayoutFieldsWithDetailsAndRowColorResponse,
            fullAttributesWithDetailsAndRowColorResponse,
            scoreDistributionFieldsResponse,
          ] = responses;
          return {
            bundleLookups: bundlesResponse.data || [],
            scoreLookups: scoresResponse.data || [],
            trendviewFieldLookups: trendviewFieldsResponse.data || [],
            addonProductLookups: addonProductsResponse.data || [],
            attributeLookups: attributesResponse.data || [],
            fullAttributeLookups: fullAttributesResponse.data || [],
            outputLayoutFieldsWithDetailsAndRowColor:
              outputLayoutFieldsWithDetailsAndRowColorResponse.data || {},
            fullAttributesWithDetailsAndRowColor:
              fullAttributesWithDetailsAndRowColorResponse.data || {},
            scoreDistributionFields: scoreDistributionFieldsResponse.data || [],
          };
        }
      })
      .then((values) => {
        const {
          bundleLookups,
          scoreLookups,
          trendviewFieldLookups,
          addonProductLookups,
          attributeLookups,
          fullAttributeLookups,
          outputLayoutFieldsWithDetailsAndRowColor,
          fullAttributesWithDetailsAndRowColor,
          scoreDistributionFields,
        } = values || {};
        setSummaryDataContainer({
          bundleLookups,
          scoreLookups,
          trendviewFieldLookups,
          addonProductLookups,
          attributeLookups,
          fullAttributeLookups,
          outputLayoutFieldsWithDetailsAndRowColor,
          fullAttributesWithDetailsAndRowColor,
          scoreDistributionFields,
        });
      })
      .catch((err) => {
        console.error(err);
        modalUtils.showError(err);
        props.closeModal();
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }, [props, summaryModel]);

  return (
    <Fragment>
      <Modal
        footer={
          isLoadingData ? (
            <></>
          ) : (
            <ProgramSummaryFooter
              model={summaryModel}
              showAccept={props.showAccept}
              onAccept={props.onAccept}
              onCancel={props.onCancel}
              onDownload={props.onDownload}
              onOk={props.onOk}
            />
          )
        }
        isOpen
        header='Program Summary'
        contentProps={{ maxW: '80%' }}
        onClose={() => {
          const closeFn = props.onCancel || props.onOk;
          closeFn && closeFn();
        }}
      >
        {summaryModel && (
          <Fragment>
            <style
              dangerouslySetInnerHTML={{
                __html: `
                  * {
                    scrollbar-width: thin;
                    scrollbar-color: #A6A6A6 #A6A6A6;
                  }
                  *::-webkit-scrollbar {
                    width: 10px;
                    height: 10px;
                  }
                  *::-webkit-scrollbar-track {
                    background: #D9D9D9;
                  }
                  *::-webkit-scrollbar-thumb {
                    background-color: #A6A6A6;
                    border-radius: 20px;
                  }

                  `,
              }}
            ></style>
            <Spin spinning={isLoadingData} tip=''>
              {isLoadingData && (
                <Text textAlign='center' mt={20}>
                  Loading program summary data...
                </Text>
              )}
              {!isLoadingData && (
                <Flex direction='column'>
                  <ExperianIcon />
                  <Text
                    as='span'
                    sx={{
                      textAlign: 'center',
                      fontSize: 16,
                      textColor: 'rgb(29, 79, 145)',
                    }}
                  >
                    Program Summary
                  </Text>
                  <OrderDetailsSummary
                    job={props.job}
                    orderDetails={summaryModel.orderDetails}
                  />
                  {summaryDataContainer && (
                    <CreditFieldsAndDeomgraphicsSummary
                      {...summaryDataContainer}
                      scores={summaryModel.scoresAndAddonProducts?.scores || []}
                      attributes={
                        summaryModel.attributesAndBundles?.attributes || []
                      }
                      fullAttributes={
                        summaryModel?.attributesAndBundles?.fullAttributes || []
                      }
                      bundles={summaryModel.attributesAndBundles?.bundles || []}
                      trendviewFields={
                        summaryModel.scoresAndAddonProducts?.trendviewFields ||
                        undefined
                      }
                      addonProducts={
                        summaryModel.scoresAndAddonProducts?.addonProducts || []
                      }
                      demographics={summaryModel.demographics}
                    />
                  )}
                  <InputFileSummary inputFile={summaryModel.inputFile} />
                  <OutputLocationsSummary
                    outputLocations={summaryModel.outputLocations || []}
                  />
                  <InquiryLoggingSummary subCode={summaryModel.subCode || ''} />
                  <ScoreDistributionReport
                    programName={summaryModel.orderDetails?.name}
                    scoreDistributionFieldsWithDetailsAndRowColor={
                      summaryDataContainer?.scoreDistributionFields || []
                    }
                  />
                  <OutputFileLayoutSummary
                    header='Output File Layout'
                    programName={summaryModel.orderDetails?.name}
                    outputLayoutFieldsWithDetailsAndRowColor={
                      summaryDataContainer?.outputLayoutFieldsWithDetailsAndRowColor ||
                      {}
                    }
                    fullAttributesWithDetailsAndRowColor={
                      summaryDataContainer?.fullAttributesWithDetailsAndRowColor ||
                      {}
                    }
                  />
                </Flex>
              )}
            </Spin>
          </Fragment>
        )}
      </Modal>
    </Fragment>
  );
};
